import React from 'react'

interface PHData {
  time: number
  value: number
}

interface PHChartProps {
  data: PHData[]
  className?: string
}

export const PHChart: React.FC<PHChartProps> = ({ data, className = '' }) => {
  const width = 400
  const height = 200
  const padding = { top: 20, right: 20, bottom: 40, left: 50 }
  const chartWidth = width - padding.left - padding.right
  const chartHeight = height - padding.top - padding.bottom

  const threshold = 5.5
  const minPH = 0
  const maxPH = 10
  const timeWindow = 60000 // 60 seconds

  const getXPosition = (timestamp: number) => {
    const now = Date.now()
    const elapsed = now - timestamp
    return chartWidth - (elapsed / timeWindow) * chartWidth
  }

  const getYPosition = (value: number) => {
    return chartHeight - ((value - minPH) / (maxPH - minPH)) * chartHeight
  }

  const getThresholdY = () => {
    return chartHeight - ((threshold - minPH) / (maxPH - minPH)) * chartHeight
  }

  // Filter data to current time window
  const filteredData = data.filter(d => Date.now() - d.time < timeWindow)

  // Create path for line chart
  const createPath = () => {
    if (filteredData.length < 2) return ''

    const points = filteredData.map(
      d => `${getXPosition(d.time)},${getYPosition(d.value)}`
    )

    return `M${points.join(' L')}`
  }

  const pathData = createPath()
  const thresholdY = getThresholdY()

  return (
    <div className={`bg-white rounded-lg ${className}`}>
      <div className="text-base font-semibold text-gray-700 mb-4">pH</div>

      <svg width={width} height={height} className="overflow-visible">
        {/* Background regions */}
        <rect
          x={padding.left}
          y={padding.top}
          width={chartWidth}
          height={thresholdY}
          style={{ fill: 'var(--color-chart-alkaline-bg)' }}
          opacity={0.5}
        />
        <rect
          x={padding.left}
          y={padding.top + thresholdY}
          width={chartWidth}
          height={chartHeight - thresholdY}
          style={{ fill: 'var(--color-chart-acidic-bg)' }}
          opacity={0.5}
        />

        {/* Grid lines */}
        {[0, 2, 4, 6, 8, 10].map(value => {
          const y = padding.top + getYPosition(value)
          return (
            <line
              key={value}
              x1={padding.left}
              y1={y}
              x2={padding.left + chartWidth}
              y2={y}
              style={{ stroke: 'var(--color-very-light-gray)' }}
              strokeWidth={1}
              opacity={0.5}
            />
          )
        })}

        {/* Threshold line */}
        <line
          x1={padding.left}
          y1={padding.top + thresholdY}
          x2={padding.left + chartWidth}
          y2={padding.top + thresholdY}
          style={{ stroke: 'var(--color-chart-acidic)' }}
          strokeWidth={2}
        />

        {/* Data line */}
        {pathData && (
          <path
            d={pathData}
            fill="none"
            style={{ stroke: 'var(--color-chart-alkaline)' }}
            strokeWidth={2}
            transform={`translate(${padding.left}, ${padding.top})`}
          />
        )}

        {/* Data points */}
        {filteredData.map((point, index) => {
          const x = padding.left + getXPosition(point.time)
          const y = padding.top + getYPosition(point.value)
          const isAboveThreshold = point.value >= threshold

          return (
            <circle
              key={index}
              cx={x}
              cy={y}
              r={3}
              style={{ fill: isAboveThreshold ? 'var(--color-chart-alkaline)' : 'var(--color-chart-acidic)' }}
            />
          )
        })}

        {/* Y-axis labels */}
        {[0, 2, 4, 5.5, 6, 8, 10].map(value => {
          const y = padding.top + getYPosition(value)
          return (
            <text
              key={value}
              x={padding.left - 10}
              y={y + 4}
              textAnchor="end"
              className="text-xs fill-gray-500"
            >
              {value}
            </text>
          )
        })}

        {/* X-axis labels */}
        <text
          x={padding.left}
          y={height - 10}
          className="text-xs fill-gray-500"
        >
          0
        </text>
        <text
          x={padding.left + chartWidth * 0.33}
          y={height - 10}
          className="text-xs fill-gray-500"
        >
          25
        </text>
        <text
          x={padding.left + chartWidth * 0.66}
          y={height - 10}
          className="text-xs fill-gray-500"
        >
          50
        </text>
        <text
          x={padding.left + chartWidth}
          y={height - 10}
          className="text-xs fill-gray-700 font-medium"
          textAnchor="end"
        >
          Seconds
        </text>
      </svg>
    </div>
  )
}

export default PHChart
